import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../services/api';
import { FaArrowLeft, FaPaperPlane, FaLock, FaFlag } from 'react-icons/fa';
import ReportModal from '../components/ReportModal';

const EMOJIS_DISPONIBLES = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
const LONG_PRESS_MS = 450;

// Paleta de colores para participantes (excluye el naranja que es del usuario propio)
const COLORES_PARTICIPANTES = [
  { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f630' }, // azul
  { color: '#22c55e', bg: '#22c55e18', border: '#22c55e30' }, // verde
  { color: '#a855f7', bg: '#a855f718', border: '#a855f730' }, // morado
  { color: '#ec4899', bg: '#ec489918', border: '#ec489930' }, // rosa
];

// Asigna un color fijo a cada userId que no sea el propio
const useColorMap = (messages, myId) => {
  return useMemo(() => {
    const map = {};
    let idx = 0;
    messages.forEach(msg => {
      if (msg.userId !== myId && !map[msg.userId]) {
        map[msg.userId] = COLORES_PARTICIPANTES[idx % COLORES_PARTICIPANTES.length];
        idx++;
      }
    });
    return map;
  }, [messages, myId]);
};

export default function Chat({ packageId, packageInfo, onBack, onOpenDetail, onViewProfile }) {
  const { token, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pickerMessageId, setPickerMessageId] = useState(null);
  const [selectorReporteAbierto, setSelectorReporteAbierto] = useState(false);
  const [reportando, setReportando] = useState(null); // { userId, userName } | null
  const bottomRef = useRef();
  const longPressTimer = useRef(null);

  const cargarMensajes = () => {
    apiFetch(`/packages/${packageId}/messages`, {}, token)
      .then(setMessages).catch(() => {});
  };

  useEffect(() => {
    cargarMensajes();
    const interval = setInterval(cargarMensajes, 3000);
    return () => clearInterval(interval);
  }, [packageId, token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cierra la barra de emojis si tocas fuera de ella
  useEffect(() => {
    if (!pickerMessageId) return;
    const cerrar = () => setPickerMessageId(null);
    document.addEventListener('click', cerrar);
    return () => document.removeEventListener('click', cerrar);
  }, [pickerMessageId]);

  const colorMap = useColorMap(messages, user?.id);

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const msg = await apiFetch(`/packages/${packageId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ text })
      }, token);
      setMessages(prev => [...prev, msg]);
      setText('');
    } catch {}
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // Mantener pulsado (funciona con dedo o ratón)
  const startLongPress = (msgId) => {
    longPressTimer.current = setTimeout(() => setPickerMessageId(msgId), LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const handleReact = async (msgId, emoji) => {
    setPickerMessageId(null);
    const msg = messages.find(m => m.id === msgId);
    const yaTenia = msg?.reactions?.find(r => r.userId === user?.id && r.emoji === emoji);

    // Actualización optimista — se ve al instante, sin esperar al servidor
    setMessages(prev => prev.map(m => {
      if (m.id !== msgId) return m;
      const sinMiReaccion = (m.reactions || []).filter(r => r.userId !== user?.id);
      return {
        ...m,
        reactions: yaTenia ? sinMiReaccion : [...sinMiReaccion, { userId: user?.id, userName: user?.name, emoji }],
      };
    }));

    try {
      if (yaTenia) {
        await apiFetch(`/packages/${packageId}/messages/${msgId}/reaction`, { method: 'DELETE' }, token);
      } else {
        await apiFetch(`/packages/${packageId}/messages/${msgId}/reaction`, {
          method: 'PUT',
          body: JSON.stringify({ emoji }),
        }, token);
      }
    } catch {
      cargarMensajes(); // si falla, recarga desde el servidor para no quedar desincronizado
    }
  };

  // Agrupar mensajes consecutivos del mismo usuario
  const grouped = messages.map((msg, i) => ({
    ...msg,
    isFirst: i === 0 || messages[i - 1].userId !== msg.userId,
    isLast:  i === messages.length - 1 || messages[i + 1].userId !== msg.userId,
  }));

  return (
    <div style={{position:'fixed', top:0, left:0, width:'100%', height:'100dvh',
      backgroundColor:'#111318', display:'flex', flexDirection:'column'}}>

      {/* Header */}
      <div style={{backgroundColor:'#1a1d24', borderBottom:'1px solid #2a2d35',
        padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px', flexShrink:0}}>
        <button onClick={onBack}
          style={{backgroundColor:'#111318', color:'#9ca3af', padding:'8px',
            border:'1px solid #2a2d35', borderRadius:'10px', display:'flex', cursor:'pointer'}}>
          <FaArrowLeft style={{fontSize:'13px'}} />
        </button>
        <div className="flex-1 cursor-pointer" onClick={onOpenDetail}>
          <p className="text-white font-semibold text-sm">Chat del envío</p>
          <p style={{color:'#f97316', fontSize:'11px'}}>
            {packageInfo?.from} → {packageInfo?.to}
          </p>
        </div>
        {/* Participantes en header */}
        <div style={{display:'flex', gap:'4px', alignItems:'center', position:'relative'}}>
          {Object.entries(colorMap).map(([uid, col]) => {
                const msg = messages.find(m => m.userId === parseInt(uid));
                return (
                  <div key={uid} onClick={() => onViewProfile?.(parseInt(uid))}
                    style={{width:'24px', height:'24px', borderRadius:'50%',
                      backgroundColor: col.color, display:'flex', alignItems:'center',
                      justifyContent:'center', fontSize:'9px', fontWeight:'bold', color:'white',
                      border:`2px solid #1a1d24`, cursor:'pointer'}}>
                    {msg?.user?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                );
              })}
          <div style={{width:'24px', height:'24px', borderRadius:'50%',
            backgroundColor:'#f97316', display:'flex', alignItems:'center',
            justifyContent:'center', fontSize:'9px', fontWeight:'bold', color:'white',
            border:'2px solid #1a1d24'}}>
            {user?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>

          {/* Botón reportar */}
          <button
            onClick={() => {
              const otros = messages
                .filter(m => m.userId !== user?.id)
                .reduce((acc, m) => {
                  if (!acc.find(p => p.id === m.userId)) acc.push({ id: m.userId, name: m.user?.name });
                  return acc;
                }, []);
              if (otros.length === 0) return;
              if (otros.length === 1) {
                setReportando({ userId: otros[0].id, userName: otros[0].name });
              } else {
                setSelectorReporteAbierto(true);
              }
            }}
            style={{backgroundColor:'#111318', border:'1px solid #2a2d35', color:'#6b7280',
              padding:'6px', borderRadius:'8px', display:'flex', cursor:'pointer', marginLeft:'4px'}}
            title="Reportar">
            <FaFlag style={{fontSize:'11px'}} />
          </button>

          {/* Selector de a quién reportar (solo si hay más de un participante) */}
          {selectorReporteAbierto && (
            <div style={{
              position:'absolute', top:'36px', right:0, backgroundColor:'#1a1d24',
              border:'1px solid #2a2d35', borderRadius:'14px', padding:'6px', minWidth:'180px',
              boxShadow:'0 12px 32px rgba(0,0,0,0.5)', zIndex:20,
            }}>
              <p style={{color:'#6b7280', fontSize:'10px', padding:'6px 10px'}}>¿A quién quieres reportar?</p>
              {Object.entries(colorMap).map(([uid]) => {
                const msg = messages.find(m => m.userId === parseInt(uid));
                return (
                  <button key={uid}
                    onClick={() => { setReportando({ userId: parseInt(uid), userName: msg?.user?.name }); setSelectorReporteAbierto(false); }}
                    style={{width:'100%', textAlign:'left', padding:'8px 10px', borderRadius:'8px',
                      background:'none', border:'none', color:'white', fontSize:'13px', cursor:'pointer'}}>
                    {msg?.user?.name}
                  </button>
                );
              })}
              <button onClick={() => setSelectorReporteAbierto(false)}
                style={{width:'100%', textAlign:'left', padding:'8px 10px', borderRadius:'8px',
                  background:'none', border:'none', color:'#6b7280', fontSize:'12px', cursor:'pointer'}}>
                Cancelar
              </button>
            </div>
          )}
        </div>
      </div>

      {reportando && (
        <ReportModal
          userId={reportando.userId}
          userName={reportando.userName}
          packageId={packageId}
          onClose={() => setReportando(null)}
        />
      )}

      {/* Aviso privacidad */}
      <div style={{backgroundColor:'#111318', padding:'8px 16px',
        borderBottom:'1px solid #1e2028', display:'flex', alignItems:'center',
        justifyContent:'center', gap:'6px'}}>
        <FaLock style={{color:'#3a3d45', fontSize:'9px'}} />
        <p style={{color:'#3a3d45', fontSize:'10px'}}>
          Solo los participantes del envío pueden ver este chat
        </p>
      </div>

      {/* Mensajes */}
      <div style={{flex:1, overflowY:'auto', padding:'16px 12px',
        display:'flex', flexDirection:'column', gap:'2px'}}>

        {messages.length === 0 && (
          <div className="text-center" style={{paddingTop:'60px'}}>
            <div style={{fontSize:'40px', opacity:0.15}} className="mb-3">💬</div>
            <p style={{color:'#6b7280', fontSize:'13px'}}>Aún no hay mensajes</p>
            <p style={{color:'#3a3d45', fontSize:'11px', marginTop:'4px'}}>
              Coordínate con los participantes del envío 🤝
            </p>
          </div>
        )}

        {grouped.map((msg) => {
          // Mensaje de sistema — se comprueba PRIMERO, como código real ejecutado,
          // antes de construir el resto del JSX.
          if (msg.system) {
            return (
              <div key={msg.id} style={{ textAlign: 'center', margin: '14px 0' }}>
                <span style={{
                  backgroundColor: '#22c55e15', color: '#22c55e',
                  fontSize: '12px', padding: '6px 14px', borderRadius: '20px',
                  border: '1px solid #22c55e25', display: 'inline-block', lineHeight: '1.4',
                }}>
                  {msg.text}
                </span>
              </div>
            );
          }

          const esMio = msg.userId === user?.id;
          const colores = esMio ? null : colorMap[msg.userId];

          // Bordes de burbuja según posición en grupo
          const borderRadius = esMio
            ? `18px 18px ${msg.isLast ? '4px' : '18px'} 18px`
            : `18px 18px 18px ${msg.isLast ? '4px' : '18px'}`;

          // Agrupar reacciones por emoji para mostrarlas compactas: "👍 2"
          const reaccionesAgrupadas = {};
          (msg.reactions || []).forEach(r => {
            reaccionesAgrupadas[r.emoji] = (reaccionesAgrupadas[r.emoji] || 0) + 1;
          });
          const miReaccion = (msg.reactions || []).find(r => r.userId === user?.id)?.emoji;

          return (
            <div key={msg.id} style={{ position: 'relative' }}>
              {pickerMessageId === msg.id && (
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    position: 'absolute', top: '-46px',
                    left: esMio ? 'auto' : '8px', right: esMio ? '8px' : 'auto',
                    backgroundColor: '#1a1d24', border: '1px solid #2a2d35',
                    borderRadius: '24px', padding: '6px 8px', display: 'flex', gap: '6px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 10,
                  }}>
                  {EMOJIS_DISPONIBLES.map(emoji => (
                    <button key={emoji} onClick={() => handleReact(msg.id, emoji)}
                      style={{
                        fontSize: '20px', background: 'none', border: 'none', cursor: 'pointer',
                        padding: '2px', lineHeight: 1,
                        transform: miReaccion === emoji ? 'scale(1.2)' : 'scale(1)',
                        transition: 'transform 0.1s',
                      }}>
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              <div style={{
                display:'flex', alignItems:'flex-end', gap:'8px',
                flexDirection: esMio ? 'row-reverse' : 'row',
                marginBottom: Object.keys(reaccionesAgrupadas).length > 0 ? '4px' : (msg.isLast ? '8px' : '2px'),
              }}>
                {/* Avatar — solo en último mensaje del grupo */}
                <div style={{width:'28px', flexShrink:0}}>
                  {!esMio && msg.isLast && (
                    msg.user?.avatar ? (
                      <img src={msg.user.avatar}
                        onClick={() => onViewProfile?.(msg.userId)}
                        style={{width:'28px', height:'28px', borderRadius:'50%',
                          objectFit:'cover', border:`2px solid ${colores?.color || '#2a2d35'}`,
                          cursor:'pointer'}} />
                    ) : (
                      <div onClick={() => onViewProfile?.(msg.userId)}
                        style={{width:'28px', height:'28px', borderRadius:'50%',
                          backgroundColor: colores?.color || '#6b7280',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:'11px', fontWeight:'bold', color:'white', cursor:'pointer'}}>
                        {msg.user?.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )
                  )}
                </div>
                {/* Burbuja */}
                <div style={{maxWidth:'72%'}}>
                  {/* Nombre — solo en primer mensaje del grupo */}
                  {!esMio && msg.isFirst && (
                    <p style={{color: colores?.color || '#9ca3af', fontSize:'10px',
                      fontWeight:'700', marginBottom:'4px', marginLeft:'4px',
                      letterSpacing:'0.02em'}}>
                      {msg.user?.name}
                    </p>
                  )}

                  <div
                    onMouseDown={() => startLongPress(msg.id)}
                    onMouseUp={cancelLongPress}
                    onMouseLeave={cancelLongPress}
                    onTouchStart={() => startLongPress(msg.id)}
                    onTouchEnd={cancelLongPress}
                    onClick={e => e.stopPropagation()}
                    style={{
                      padding:'9px 13px', borderRadius, cursor: 'pointer', userSelect: 'none',
                      ...(esMio
                        ? {backgroundColor:'#f97316', color:'white'}
                        : {backgroundColor: colores?.bg || '#1a1d24',
                           color:'white',
                           border:`1px solid ${colores?.border || '#2a2d35'}`}
                      )
                    }}>
                    <p style={{fontSize:'14px', lineHeight:'1.5', wordBreak:'break-word'}}>
                      {msg.text}
                    </p>
                    {msg.isLast && (
                      <p style={{fontSize:'10px', marginTop:'3px',
                        color: esMio ? 'rgba(255,255,255,0.55)' : '#6b7280',
                        textAlign: esMio ? 'right' : 'left'}}>
                        {new Date(msg.createdAt).toLocaleTimeString('es-ES',
                          {hour:'2-digit', minute:'2-digit'})}
                      </p>
                    )}
                  </div>

                  {/* Reacciones puestas al mensaje */}
                  {Object.keys(reaccionesAgrupadas).length > 0 && (
                    <div style={{
                      display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap',
                      justifyContent: esMio ? 'flex-end' : 'flex-start',
                    }}>
                      {Object.entries(reaccionesAgrupadas).map(([emoji, count]) => (
                        <button key={emoji} onClick={() => handleReact(msg.id, emoji)}
                          style={{
                            backgroundColor: miReaccion === emoji ? '#f9731620' : '#1a1d24',
                            border: `1px solid ${miReaccion === emoji ? '#f97316' : '#2a2d35'}`,
                            borderRadius: '12px', padding: '2px 8px', fontSize: '12px',
                            display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer',
                          }}>
                          <span>{emoji}</span>
                          {count > 1 && <span style={{ color: '#9ca3af', fontSize: '10px' }}>{count}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{backgroundColor:'#1a1d24', borderTop:'1px solid #2a2d35',
        padding:'12px 16px', flexShrink:0}}>
        <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribe un mensaje..."
            maxLength={2000}
            style={{flex:1, minWidth:0, backgroundColor:'#111318', border:'1px solid #2a2d35',
              color:'white', borderRadius:'20px', padding:'10px 16px', fontSize:'14px'}}
            className="focus:outline-none focus:ring-1 focus:ring-orange-500 placeholder-gray-600"
          />
          <button onClick={handleSend}
            disabled={sending || !text.trim()}
            style={{backgroundColor: text.trim() ? '#f97316' : '#2a2d35',
              color:'white', borderRadius:'50%', width:'40px', height:'40px',
              display:'flex', alignItems:'center', justifyContent:'center',
              flexShrink:0, transition:'background-color 0.2s',
              opacity: sending ? 0.5 : 1, cursor: text.trim() ? 'pointer' : 'default'}}>
            <FaPaperPlane style={{fontSize:'13px'}} />
          </button>
        </div>
        {text.length > 1800 && (
          <p style={{color: text.length >= 2000 ? '#f87171' : '#6b7280', fontSize:'10px',
            marginTop:'4px', textAlign:'right'}}>
            {text.length}/2000
          </p>
        )}
      </div>
    </div>
  );
}